// Pure assembly for the Summary → Report tab. Combines the (client-side) summary
// and work-status ("applications") data into a per-employee report scoped to a
// picked date range. No network, no React — kept pure so it's unit-testable and
// so the same maths that powers the Summary/Dashboard (computeEmployeeStats) is
// the single source of truth for the report's headline figures too.

import { computeEmployeeStats } from '../utils/summaryStats';
import { getDateFromWeekAndDay } from '../utils/weekPlanUtils';

const DAY_MS = 86400000;

const sameEmail = (a, b) =>
  (a || '').trim().toLowerCase() === (b || '').trim().toLowerCase();

// Monday (UTC midnight) of an ISO week. ISO week 1 is the week containing Jan 4.
const isoWeekMonday = (year, week) => {
  const jan4 = new Date(Date.UTC(year, 0, 4));
  const jan4Dow = (jan4.getUTCDay() + 6) % 7; // Mon=0 … Sun=6
  const mondayOfWeek1 = jan4.getTime() - jan4Dow * DAY_MS;
  return new Date(mondayOfWeek1 + (week - 1) * 7 * DAY_MS);
};

/**
 * The [Monday 00:00, Sunday 23:59:59.999] span (UTC) of an ISO week, so a
 * `SummaryCalendarDTO` (year + weekCalendarNumber) can be tested for overlap
 * with a picked date range.
 */
export const isoWeekDateRange = (year, week) => {
  const monday = isoWeekMonday(year, week);
  const sunday = new Date(monday.getTime() + 7 * DAY_MS - 1);
  return { monday, sunday };
};

const weekIntersectsRange = (cal, from, to) => {
  const { monday, sunday } = isoWeekDateRange(cal.year, cal.weekCalendarNumber);
  return sunday >= from && monday <= to;
};

const applicationIntersectsRange = (app, from, to) => {
  const af = new Date(app.dateFrom);
  const at = new Date(app.dateTo);
  // Guard against unparseable dates rather than letting NaN comparisons pass.
  if (Number.isNaN(af.getTime()) || Number.isNaN(at.getTime())) return false;
  return at >= from && af <= to;
};

/**
 * Build the report model.
 *
 * @param {object} args
 * @param {object[]} args.summaryEmployees - SummaryEmployeeDTO[] (self for a user,
 *   whole org for an admin — the caller does not pre-filter).
 * @param {object[]} args.applications - WorkStatusRequestDTO[] (self or whole org).
 * @param {string[]|null} [args.employeeEmails] - restrict to these employees; null
 *   or empty means "all present" (the admin "all employees" / the user's self case).
 * @param {Date} args.from - range start.
 * @param {Date} args.to - range end.
 * @returns {{ employees: Array<{employee, stats, weeks, applications}>, from: Date, to: Date }}
 */
export const buildReport = ({
  summaryEmployees = [],
  applications = [],
  employeeEmails = null,
  from,
  to,
} = {}) => {
  const wanted =
    employeeEmails && employeeEmails.length
      ? new Set(employeeEmails.map((e) => (e || '').trim().toLowerCase()))
      : null;

  const employees = summaryEmployees
    .filter((emp) => !wanted || wanted.has((emp.email || '').trim().toLowerCase()))
    .map((employee) => {
      const weeks = (employee.summaryCalendarEmployees || [])
        .filter((cal) => weekIntersectsRange(cal, from, to))
        .slice()
        .sort((a, b) =>
          a.year !== b.year
            ? a.year - b.year
            : a.weekCalendarNumber - b.weekCalendarNumber
        );

      // Reuse the Summary/Dashboard maths over exactly the in-range weeks so the
      // report's figures never drift from the rest of the app.
      const stats = computeEmployeeStats({
        ...employee,
        summaryCalendarEmployees: weeks,
      });

      const apps = applications
        .filter(
          (a) =>
            sameEmail(a.email, employee.email) &&
            applicationIntersectsRange(a, from, to)
        )
        .slice()
        .sort((a, b) => new Date(b.dateFrom) - new Date(a.dateFrom));

      return { employee, stats, weeks, applications: apps };
    });

  return { employees, from, to };
};

/**
 * The distinct `{year, week}` pairs across every employee's in-range weeks — the
 * set of week plans to fetch for notices. A commented day always belongs to a
 * planned week, so these are the only weeks that can carry notices.
 */
export const distinctWeeks = (reportEmployees = []) => {
  const seen = new Set();
  const out = [];
  reportEmployees.forEach(({ weeks = [] }) => {
    weeks.forEach((w) => {
      const key = `${w.year}-${w.weekCalendarNumber}`;
      if (!seen.has(key)) {
        seen.add(key);
        out.push({ year: w.year, week: w.weekCalendarNumber });
      }
    });
  });
  return out;
};

/**
 * Attach the week-plan "notices" (per-day comments) to each employee in a built
 * report. `weekPlans` is a flat array of WeekPlanEmployeeDTO (all fetched weeks,
 * possibly for many employees); notices are matched to each report employee by
 * email, limited to non-empty comments on days within [from, to], sorted by date.
 *
 * @returns a new report object; input is not mutated.
 */
export const attachNotices = (report, weekPlans = [], { from, to } = {}) => {
  const employees = (report.employees || []).map((entry) => {
    const notices = [];
    weekPlans
      .filter((wp) => sameEmail(wp.email, entry.employee.email))
      .forEach((wp) => {
        (wp.calendarEmployees || []).forEach((cal) => {
          (cal.dailyPlanEmployees || []).forEach((dp) => {
            const comment = (dp.comment || '').trim();
            if (!comment) return;
            let date;
            try {
              date = getDateFromWeekAndDay(cal.year, cal.weekCalendarNumber, dp.day);
            } catch {
              return; // unknown weekday — skip rather than crash the report
            }
            if (from && date < from) return;
            if (to && date > to) return;
            notices.push({
              year: cal.year,
              week: cal.weekCalendarNumber,
              day: dp.day,
              date,
              comment,
            });
          });
        });
      });
    notices.sort((a, b) => a.date - b.date);
    return { ...entry, notices };
  });

  return { ...report, employees };
};
